import type {
  DeviceRecord,
  DirectEpochTransitionClaim,
  DirectEpochTransitionPayload,
  DirectEpochTransitionReason,
  GroupEpochTransitionClaim,
  GroupEpochTransitionPayload,
  GroupEpochTransitionReason,
  IdentityId,
  PublicKey
} from '@shared/types';
import { createLegacySignatureMessage, createSignatureMessage } from '@shared/signatureMessage.ts';
import { signRequestWithKey } from '../crypto/signing';
import type { SigningKey } from '../crypto/ed25519Provider';
import { verifyEd25519 } from '../crypto/ed25519Provider';
import { verifyDeviceRegistrationAttestation } from '../crypto/deviceKeyBinding';
import { base64ToBytes } from '../utils/encoding';
import { logChatCrypto } from '../utils/chatCryptoDebug';

export async function signGroupEpochTransition(params: {
  chatId: string;
  epoch: number;
  previousEpoch: number | null;
  keyCommitment: string;
  proposerIdentityId: IdentityId;
  proposerDeviceId: string;
  reason: GroupEpochTransitionReason;
  signingKey: SigningKey;
}): Promise<GroupEpochTransitionClaim> {
  return signRequestWithKey<GroupEpochTransitionPayload>(
    'grp:epoch-transition',
    {
      version: 1,
      purpose: 'group-epoch-transition-v1',
      chatId: params.chatId,
      epoch: params.epoch,
      previousEpoch: params.previousEpoch,
      keyCommitment: params.keyCommitment,
      proposerIdentityId: params.proposerIdentityId,
      proposerDeviceId: params.proposerDeviceId,
      reason: params.reason
    },
    params.proposerDeviceId,
    params.signingKey
  ) as Promise<GroupEpochTransitionClaim>;
}

async function verifySignedClaimWithPublicKey(
  claim: GroupEpochTransitionClaim | DirectEpochTransitionClaim,
  publicKey: PublicKey
): Promise<boolean> {
  if (publicKey.algorithm !== 'ed25519') return false;
  const { signature, ...unsigned } = claim;
  const message = createSignatureMessage(unsigned as any);
  const signatureBytes = base64ToBytes(signature);
  const publicKeyBytes = base64ToBytes(publicKey.value);
  const canonicalValid = await verifyEd25519(
    new TextEncoder().encode(message),
    signatureBytes,
    publicKeyBytes
  );
  if (canonicalValid) return true;
  const legacyMessage = createLegacySignatureMessage(unsigned as any);
  if (legacyMessage === message) return false;
  return verifyEd25519(
    new TextEncoder().encode(legacyMessage),
    signatureBytes,
    publicKeyBytes
  );
}

export async function signDirectEpochTransition(params: {
  directChatId: string;
  participantIdentityIds: [IdentityId, IdentityId];
  epoch: number;
  previousEpoch: number | null;
  keyCommitment: string;
  proposerIdentityId: IdentityId;
  proposerDeviceId: string;
  reason: DirectEpochTransitionReason;
  signingKey: SigningKey;
}): Promise<DirectEpochTransitionClaim> {
  return signRequestWithKey<DirectEpochTransitionPayload>(
    'dir:epoch-transition',
    {
      version: 1,
      purpose: 'direct-epoch-transition-v1',
      directChatId: params.directChatId,
      participantIdentityIds: params.participantIdentityIds,
      epoch: params.epoch,
      previousEpoch: params.previousEpoch,
      keyCommitment: params.keyCommitment,
      proposerIdentityId: params.proposerIdentityId,
      proposerDeviceId: params.proposerDeviceId,
      reason: params.reason
    },
    params.proposerDeviceId,
    params.signingKey
  ) as Promise<DirectEpochTransitionClaim>;
}

export async function verifyGroupEpochTransition(params: {
  claim: GroupEpochTransitionClaim | null | undefined;
  chatId: string;
  epoch: number;
  previousEpoch: number | null;
  keyCommitment: string;
  participantIdentityIds: string[];
  identityPublicKeys: Record<string, PublicKey>;
  devicesByIdentityId: Record<string, DeviceRecord[]>;
  allowUnattestedProposer?: boolean;
}): Promise<boolean> {
  const { claim } = params;
  if (!claim || claim.type !== 'grp:epoch-transition') return false;
  const payload = claim.payload;
  if (
    payload.version !== 1 ||
    payload.purpose !== 'group-epoch-transition-v1' ||
    payload.chatId !== params.chatId ||
    payload.epoch !== params.epoch ||
    payload.previousEpoch !== params.previousEpoch ||
    payload.keyCommitment !== params.keyCommitment ||
    claim.signerId !== payload.proposerDeviceId ||
    !params.participantIdentityIds.includes(payload.proposerIdentityId)
  ) {
    return false;
  }

  const proposerDevices = params.devicesByIdentityId[payload.proposerIdentityId] || [];
  const proposerDevice = proposerDevices.find((device) => device.deviceId === payload.proposerDeviceId);
  const identityPublicKey = params.identityPublicKeys[payload.proposerIdentityId];
  if (!proposerDevice || !identityPublicKey) return false;

  const attestationValid = await verifyDeviceRegistrationAttestation({
    attestation: proposerDevice.registrationAttestation,
    identityId: payload.proposerIdentityId,
    identityPublicKey,
    devicePublicKey: proposerDevice.publicKey,
    deviceEncryptionPublicKey: proposerDevice.encryptionPublicKey
  }).catch(() => false);
  if (!attestationValid && !params.allowUnattestedProposer) return false;

  return verifySignedClaimWithPublicKey(claim, proposerDevice.publicKey);
}

export async function verifyDirectEpochTransition(params: {
  claim: DirectEpochTransitionClaim | null | undefined;
  directChatId: string;
  participantIdentityIds: [string, string];
  epoch: number;
  previousEpoch: number | null;
  keyCommitment: string;
  identityPublicKeys: Record<string, PublicKey>;
  devicesByIdentityId: Record<string, DeviceRecord[]>;
  allowUnattestedProposer?: boolean;
}): Promise<boolean> {
  const { claim } = params;
  if (!claim || claim.type !== 'dir:epoch-transition') {
    logChatCrypto('direct', 'transition-invalid-claim', {
      directChatId: params.directChatId,
      claimType: claim?.type ?? null
    });
    return false;
  }
  const payload = claim.payload;
  const expectedParticipants = [...params.participantIdentityIds].sort();
  const payloadParticipants = [...payload.participantIdentityIds].sort();
  if (
    payload.version !== 1 ||
    payload.purpose !== 'direct-epoch-transition-v1' ||
    payload.directChatId !== params.directChatId ||
    payload.epoch !== params.epoch ||
    payload.previousEpoch !== params.previousEpoch ||
    payload.keyCommitment !== params.keyCommitment ||
    claim.signerId !== payload.proposerDeviceId ||
    payloadParticipants[0] !== expectedParticipants[0] ||
    payloadParticipants[1] !== expectedParticipants[1] ||
    !expectedParticipants.includes(payload.proposerIdentityId)
  ) {
    logChatCrypto('direct', 'transition-payload-mismatch', {
      directChatId: params.directChatId,
      epoch: params.epoch,
      payloadVersion: payload.version,
      payloadPurpose: payload.purpose,
      payloadDirectChatId: payload.directChatId,
      payloadEpoch: payload.epoch,
      expectedEpoch: params.epoch,
      payloadPreviousEpoch: payload.previousEpoch,
      expectedPreviousEpoch: params.previousEpoch,
      payloadKeyCommitment: payload.keyCommitment,
      expectedKeyCommitment: params.keyCommitment,
      signerId: claim.signerId,
      proposerDeviceId: payload.proposerDeviceId,
      payloadParticipants,
      expectedParticipants,
      proposerIdentityId: payload.proposerIdentityId
    });
    return false;
  }

  const proposerDevices = params.devicesByIdentityId[payload.proposerIdentityId] || [];
  const proposerDevice = proposerDevices.find((device) => device.deviceId === payload.proposerDeviceId);
  const identityPublicKey = params.identityPublicKeys[payload.proposerIdentityId];
  if (!proposerDevice || !identityPublicKey) {
    logChatCrypto('direct', 'transition-missing-proposer-context', {
      directChatId: params.directChatId,
      epoch: params.epoch,
      proposerIdentityId: payload.proposerIdentityId,
      proposerDeviceId: payload.proposerDeviceId,
      hasProposerDevice: Boolean(proposerDevice),
      hasIdentityPublicKey: Boolean(identityPublicKey),
      proposerDeviceCount: proposerDevices.length
    });
    return false;
  }

  const attestationValid = await verifyDeviceRegistrationAttestation({
    attestation: proposerDevice.registrationAttestation,
    identityId: payload.proposerIdentityId,
    identityPublicKey,
    devicePublicKey: proposerDevice.publicKey,
    deviceEncryptionPublicKey: proposerDevice.encryptionPublicKey
  }).catch(() => false);
  if (!attestationValid) {
    logChatCrypto('direct', 'transition-attestation-invalid', {
      directChatId: params.directChatId,
      epoch: params.epoch,
      proposerIdentityId: payload.proposerIdentityId,
      proposerDeviceId: payload.proposerDeviceId,
      hasRegistrationAttestation: Boolean(proposerDevice.registrationAttestation),
      hasEncryptionPublicKey: Boolean(proposerDevice.encryptionPublicKey)
    });
    return false;
  }

  const claimSignatureValid = await verifySignedClaimWithPublicKey(claim, proposerDevice.publicKey);
  if (!claimSignatureValid) {
    logChatCrypto('direct', 'transition-claim-signature-invalid', {
      directChatId: params.directChatId,
      epoch: params.epoch,
      proposerIdentityId: payload.proposerIdentityId,
      proposerDeviceId: payload.proposerDeviceId
    });
  }
  return claimSignatureValid;
}
